"use client";

import * as React from "react";

// @components
import { ChartBlock } from "@/components";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

// @hooks
import { useVariation } from "../store";
import { useRawData } from "@/hooks/use-liquidity";

// @utils
import { generateColor, pieChartOuter } from "@/utils";

export function BidAndAsk() {
  const { data, isLoading } = useRawData();
  const [activeBidAsk, setActiveBidAsk] = React.useState(0);
  const variation = useVariation((state: any) => state.variation);

  const ask = React.useMemo(() => {
    if (!isLoading) {
      if (variation !== "EHEX & PHEX") {
        return data
          .map((x: Record<string, any>) => {
            if (
              x.name0.toUpperCase() === variation ||
              x.name1.toUpperCase() === variation
            ) {
              return x.ask;
            }
          })
          .filter((el: any) => el !== undefined)
          .reduce((x: number, y: number) => x + y, 0);
      }

      return data
        .map((x: Record<string, any>) => x.ask)
        .reduce((x: number, y: number) => x + y, 0);
    }
  }, [data, isLoading, variation]);

  const bid = React.useMemo(() => {
    if (!isLoading) {
      if (variation !== "EHEX & PHEX") {
        return data
          .map((x: Record<string, any>) => {
            if (
              x.name0.toUpperCase() === variation ||
              x.name1.toUpperCase() === variation
            ) {
              return x.bid;
            }
          })
          .filter((el: any) => el !== undefined)
          .reduce((x: number, y: number) => x + y, 0);
      }

      return data
        .map((x: Record<string, any>) => x.bid)
        .reduce((x: number, y: number) => x + y, 0);
    }
  }, [data, isLoading, variation]);

  const total = React.useMemo(() => ask + bid, [ask, bid]);

  const chartData = React.useMemo(
    () => [
      {
        name: "Bid",
        liquidity: bid,
        percentage: (bid / total) * 100,
      },
      {
        name: "Ask",
        liquidity: ask,
        percentage: (ask / total) * 100,
      },
    ],
    [ask, bid, total],
  );

  function onPieEnter(_: any, index: number) {
    setActiveBidAsk(index);
  }

  const colors = React.useMemo(
    () => chartData?.map(() => generateColor()),
    [chartData],
  );

  return (
    <ChartBlock
      isSmall
      isRounded
      title="Bid & Ask"
      isLoading={isLoading}
      watermark={false}
      className="overflow-visible"
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={120}
            data={chartData}
            dataKey="liquidity"
            stroke="transparent"
            activeIndex={activeBidAsk}
            activeShape={pieChartOuter}
            onMouseEnter={onPieEnter}
          >
            {chartData?.map((_: any, index: number) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </ChartBlock>
  );
}
